import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/api/client';

export interface User {
  id: string;
  email: string;
  displayName?: string;
  roles?: string[];
  companyId?: string;
  company?: string;
  org?: string;
  role?: string;
  location?: string;
  plan?: string;
  name?: string;
  createdAt?: any;
  updatedAt?: any;
  [key: string]: any; // Pour autres champs Firestore
}

export interface UsersQueryOptions {
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  role?: string;
}

interface UseUsersResult {
  users: User[];
  loading: boolean;
  error: Error | null;
  total: number;
  limit: number;
  offset: number;
  refetch: () => Promise<void>;
}

/**
 * Hook réutilisable pour récupérer la liste des utilisateurs via l'API backend
 * Supporte le filtrage, tri et pagination côté serveur
 *
 * @example
 * ```tsx
 * // Usage basique
 * const { users, loading, error, total } = useUsers();
 *
 * // Avec recherche
 * const { users, loading } = useUsers({ search: 'john' });
 *
 * // Avec tri
 * const { users } = useUsers({ sortBy: 'displayName', sortOrder: 'asc' });
 *
 * // Avec pagination
 * const { users, total } = useUsers({ limit: 50, offset: 0 });
 *
 * // Filtrer par rôle
 * const { users } = useUsers({ role: 'buyer' });
 * ```
 *
 * @param {UsersQueryOptions} options - Query options (search, sortBy, sortOrder, limit, offset, role)
 * @returns {UseUsersResult} Object with users, loading, error, pagination info, and refetch function
 */
export function useUsers(options?: UsersQueryOptions): UseUsersResult {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Construire les query params
      const params = new URLSearchParams();
      if (options?.search) params.append('search', options.search);
      if (options?.sortBy) params.append('sortBy', options.sortBy);
      if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.role) params.append('role', options.role);

      const queryString = params.toString();
      const url = queryString ? `/users?${queryString}` : '/users';

      const response = await apiClient.get(url);

      // Backend retourne: { users, total, limit, offset }
      setUsers(response.data.users);
      setTotal(response.data.total);
      setLimit(response.data.limit);
      setOffset(response.data.offset);
    } catch (err: any) {
      console.error('[useUsers] Error fetching users:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [options?.search, options?.sortBy, options?.sortOrder, options?.limit, options?.offset, options?.role]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    total,
    limit,
    offset,
    refetch: fetchUsers,
  };
}

/**
 * Hook réutilisable pour récupérer un utilisateur spécifique par ID
 *
 * @example
 * ```tsx
 * const { user, loading, error, refetch } = useUser('abc123');
 * ```
 */
export function useSingleUser(userId: string | null) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/users/${userId}`);
      setUser(response.data);
    } catch (err: any) {
      console.error(`[useSingleUser] Error fetching user ${userId}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    user,
    loading,
    error,
    refetch: fetchUser,
  };
}
